import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Users,
  Clock,
  CheckCircle,
  XCircle,
  LogOut,
  Trash2,
  Check,
  X,
  ArrowLeft,
  LayoutDashboard,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "/assets/logo.png";
const TOKEN_KEY = "asx_admin_token";

const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
});

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-zinc-950/50 border border-white/5 p-6" data-testid={`stat-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs tracking-[0.2em] uppercase text-zinc-500 font-medium">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="font-heading text-3xl font-medium text-white">{value}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  // Check auth on mount
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setIsAuthenticated(false);
      return;
    }
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, authConfig());
        setUser(response.data);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/waitlist`, authConfig()),
        axios.get(`${API}/admin/stats`, authConfig()),
      ]);
      setEntries(entriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchData();
    }
  }, [isAuthenticated, user, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    
    
    
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      
      setUser(res.data);
      
      setIsAuthenticated(true);
    
    
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login failed. Please try again.");
    }
    setLoggingIn(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authConfig());
    } catch {
      // ignore
    }
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const handleStatusUpdate = async (entryId, status) => {
    try {
      await axios.patch(`${API}/admin/waitlist/${entryId}/status`, { status }, authConfig());
      fetchData();
    } catch (error) {
      console.error("Failed to update:", error);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/admin/waitlist/${entryId}`, authConfig());
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
    }
  };

  const filteredEntries =
    filter === "all" ? entries : entries.filter((e) => e.status === filter);

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      approved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      rejected: "bg-red-500/10 text-red-400 border-red-500/20",
    };
    return (
      <span
        className={`inline-flex px-2.5 py-0.5 text-xs font-medium border ${styles[status] || styles.pending}`}
        data-testid={`status-badge-${status}`}
      >
        {status}
      </span>
    );
  };

  // --- LOGIN PAGE ---
  if (isAuthenticated === false) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center px-6">
        <div className="text-center max-w-sm w-full" data-testid="admin-login-page">
          <img src={LOGO} alt="AirStageX" className="h-14 mx-auto mb-8" />
          <h1 className="font-heading text-2xl font-medium text-white mb-3">
            Admin Access
          </h1>
          <p className="text-zinc-500 text-sm mb-10">
            Sign in to manage AirStageX.
          </p>
          <form onSubmit={handleLogin} className="space-y-4 text-left">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full bg-zinc-950/60 border border-white/10 px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#007AFF] transition-colors"
              data-testid="admin-email-input"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full bg-zinc-950/60 border border-white/10 px-4 py-3 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#007AFF] transition-colors"
              data-testid="admin-password-input"
            />
            {loginError && (
              <p className="text-red-400 text-xs" data-testid="admin-login-error">
                {loginError}
              </p>
            )}
            <button
              type="submit"
              disabled={loggingIn}
              className="btn-electric bg-[#007AFF] text-white px-8 py-4 w-full font-medium tracking-wide hover:bg-[#3395FF] transition-colors text-sm disabled:opacity-60"
              data-testid="admin-login-btn"
            >
              {loggingIn ? "Signing in…" : "Sign In"}
            </button>
          </form>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-500 text-xs mt-8 hover:text-white transition-colors"
            data-testid="admin-back-home-link"
          >
            <ArrowLeft className="w-3 h-3" />
            Back to homepage
          </a>
        </div>
      </div>
    );
  }

  // --- LOADING PAGE ---
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // --- DASHBOARD ---
  return (
    <div className="min-h-screen bg-[#030303] text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="border-b border-white/5 py-4 px-6 sm:px-12">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/">
              <img src={LOGO} alt="AirStageX" className="h-8" />
            </a>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4 text-[#007AFF]" />
              <span className="text-sm font-medium">Admin</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-zinc-400 text-sm hidden sm:block">
              {user?.name || user?.email}
            </span>
            <button
              onClick={handleLogout}
              className="text-zinc-500 hover:text-white transition-colors p-1"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 sm:px-12 py-8">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total" value={stats.total} icon={Users} color="text-[#007AFF]" />
            <StatCard label="Pending" value={stats.pending} icon={Clock} color="text-yellow-400" />
            <StatCard label="Approved" value={stats.approved} icon={CheckCircle} color="text-emerald-400" />
            <StatCard label="Rejected" value={stats.rejected} icon={XCircle} color="text-red-400" />
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-white/5 pb-4">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-xs font-medium tracking-wide uppercase transition-colors ${
                filter === f
                  ? "text-white bg-white/5"
                  : "text-zinc-500 hover:text-white"
              }`}
              data-testid={`filter-${f}`}
            >
              {f}
            </button>
          ))}
          <div className="ml-auto text-zinc-500 text-xs">
            {filteredEntries.length} entries
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-6 h-6 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-16" data-testid="no-entries-message">
            <p className="text-zinc-500 text-sm">No entries found.</p>
          </div>
        ) : (
          <div className="border border-white/5 overflow-x-auto" data-testid="waitlist-table">
            <Table>
              <TableHeader>
                <TableRow className="border-white/5 hover:bg-transparent">
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium">Name</TableHead>
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium">Email</TableHead>
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium hidden md:table-cell">Location</TableHead>
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium hidden lg:table-cell">Budget</TableHead>
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium">Status</TableHead>
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium hidden sm:table-cell">Date</TableHead>
                  <TableHead className="text-zinc-500 text-xs tracking-wider uppercase font-medium text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow
                    key={entry.entry_id}
                    className="border-white/5 hover:bg-white/[0.02]"
                    data-testid={`waitlist-row-${entry.entry_id}`}
                  >
                    <TableCell className="text-white text-sm font-medium">{entry.name}</TableCell>
                    <TableCell className="text-zinc-400 text-sm">{entry.email}</TableCell>
                    <TableCell className="text-zinc-500 text-sm hidden md:table-cell">{entry.location || "-"}</TableCell>
                    <TableCell className="text-zinc-500 text-sm hidden lg:table-cell">{entry.spending_range || "-"}</TableCell>
                    <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    <TableCell className="text-zinc-600 text-xs hidden sm:table-cell">
                      {new Date(entry.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {entry.status !== "approved" && (
                          <button
                            onClick={() => handleStatusUpdate(entry.entry_id, "approved")}
                            className="p-1.5 text-zinc-500 hover:text-emerald-400 transition-colors"
                            title="Approve"
                            data-testid={`approve-${entry.entry_id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                        )}
                        {entry.status !== "rejected" && (
                          <button
                            onClick={() => handleStatusUpdate(entry.entry_id, "rejected")}
                            className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                            title="Reject"
                            data-testid={`reject-${entry.entry_id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(entry.entry_id)}
                          className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                          title="Delete"
                          data-testid={`delete-${entry.entry_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </main>
    </div>
  );
}
